import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, X } from 'lucide-react';

interface FinalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function FinalizeModal({ isOpen, onClose, onConfirm }: FinalizeModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gradient-to-b from-[#0a0f1f] to-[#0c1222] border border-[#1a2d4d] rounded-xl max-w-md w-full"
          >
            {/* Header */}
            <div className="bg-[#0a0f1f] border-b border-[#1a2d4d] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <h2 className="text-xl font-bold text-white">Finalize Story</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-2"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="text-center space-y-3">
                <div className="w-16 h-16 mx-auto bg-green-900/30 border border-green-600/50 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>

                <h3 className="text-lg font-semibold text-white">Your story is complete!</h3>

                <p className="text-gray-300 text-sm leading-relaxed">
                  {`You've successfully written both the Better World and Worse World paths. Are you`}
                  ready to finalize your interactive story?
                </p>

                <div className="bg-[#1a223a] border border-[#2a3756] rounded-lg p-3 text-left">
                  <p className="text-gray-400 text-xs">
                    <strong className="text-gray-300">Note:</strong> Once finalized, you can still
                    {`export your story, but you won't be able to make further edits without starting`}
                    over.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-[#0a0f1f] border-t border-[#1a2d4d] px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Continue Editing
              </button>

              <button
                onClick={onConfirm}
                className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-500 text-white font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all"
              >
                Finalize Story
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
